import * as React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Tabs,
  Tab,
  Box,
  Typography,
  Chip,
  Divider,
  Paper,
  Grid,
  Card,
  CardContent,
  Alert
} from "@mui/material"
import axios from 'axios';
import amplitude from "../../utils/ampli";
import Cookies from 'js-cookie'
import { useDispatch, useSelector } from 'react-redux';
import { apiSlice } from '../../redux/slices/apiSlice';
import { setSeverity , setAlertText, setToast} from '../../redux/slices/commonComponentSlice';
import { startProgress } from '../../redux/slices/progressSlice';
import { convertToJsonObject, cn, mapping } from '../../utils/utils';
import { pricing } from '../../redux/slices/pricingSlice';
import { auth } from '../../redux/slices/authSlice';
import { useGetFilteredDataMutation } from '../../redux/slices/apiSlice';
import { LockKeyhole, Loader2, BadgePercent, CheckCircle, AlertCircle, AlertTriangle, TrendingDown, TrendingUp, X } from 'lucide-react';
import { useState, useEffect, useRef, useCallback } from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';

function TailwindTabs({ value, onChange }) {
  const tabs = [
    { id: 0, label: "PPC" },
    { id: 1, label: "Total Price" },
    { id: 2, label: "Rap Disc" },
    { id: 3, label: "Rank" }
  ]

  return (
    <div className="w-full border-b border-gray-500">
      <div className="flex border border-gray-100 bg-transparent px-1 gap-1 py-1 rounded-md justify-between items-center">
        {tabs.map((tab) => (
          <div
            key={tab.id}
            onClick={() => onChange(null, tab.id)}
            className={`
              relative px-1 py-[0.35rem] text-center w-full rounded-md text-xs font-semibold transition-colors duration-200
               cursor-pointer
              ${value === tab.id 
                ? 'bg-blue-600 border-b-2 text-white border-blue-600 bg-blue-50/30 hover:bg-blue-700' 
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
              }
            `}
          >
            {tab.label}
          </div>
        ))}
      </div>
    </div>
  )
}

const BulkRepricing = ({selectedItems, filters, handleBulkClose, searchParam, allSelected, activeTab,configSort, id}) => {
  const [pricingMethod, setPricingMethod] = useState("to")
  const [priceType, setPriceType] = useState(0) // 0: ppc, 1: total, 2: rap
  const [rankType, setRankType] = useState("rank") // 0: ppc, 1: total, 2: rap
  const [value, setValue] = useState("");
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingChangePercent, setPendingChangePercent] = useState(null);
  const [pendingMetric, setPendingMetric] = useState("");
  const [limitExceeded, setLimitExceeded] = useState(false);
  const [limitAck, setLimitAck] = useState(false);
  
  // Progress tracking states (local only for button disabling)
  const [isProcessing, setIsProcessing] = useState(false);  
  const dispatch = useDispatch();
  const sorting = useSelector(pricing).sorting;
  const page = useSelector(pricing).page;
  const pageSize = useSelector(pricing).pageSize;
  const accountId = useSelector(auth)?.userDetails?.account_id;
  const max_reprice_limit = useSelector(pricing)?.config?.max_pcp_reprice_limit;

  const [refetchFiltered] = useGetFilteredDataMutation();

  // Local progress vars removed in favor of global progress bar
  const [progress, setProgress] = useState(0);
  const [channelId, setChannelId] = useState(null);
  const pollRef = useRef(null);

  
  const customFiltersRaw = useSelector(pricing)?.config?.custom_filters ?? [];
  let newFilters = convertToJsonObject(filters, customFiltersRaw);

  // Move this before the useEffect that uses it
  const { uniqueLDIDs, uniqueCertNums, uniquePRRIDs, uniquePRR1IDs } = React.useMemo(() => {
    const ldIds = new Set();
    const certNums = new Set();
    const prrNums = new Set();
    selectedItems.forEach(item => {
      ldIds.add(item["LD ID"]);
      certNums.add(item["Cert Num"]);
      prrNums.add(item["Item ID"]);
    });
    if (allSelected) {
      return { uniqueLDIDs: ["All"], uniqueCertNums: ["All"], uniquePRRIDs: ["All"], uniquePRR1IDs: Array.from(prrNums)};
    }

    return { 
      uniqueLDIDs: Array.from(ldIds),
      uniqueCertNums: Array.from(certNums),
      uniquePRRIDs: Array.from(prrNums)
    };
  }, [selectedItems, allSelected]);


  const handleClickOpen = () => {
    setOpen(true);
    // Reset progress states
    setIsProcessing(false);
    setPendingChangePercent(null);
    setPendingMetric("");
    setLimitExceeded(false);
    setLimitAck(false);
  };

  const handleClose = () => {
    setOpen(false);
    // Reset states
    setIsLoading(false);
    setIsProcessing(false);
    setPendingChangePercent(null);
    setPendingMetric("");
    setLimitExceeded(false);
    setLimitAck(false);
  };



  useEffect(() => {
    if (value !== "") {
      evaluateLimit(value);
    }
  }, [priceType, pricingMethod]); // re-evaluate when the pricing mode changes

  const evaluateLimit = (candidateVal) => {
    if (candidateVal === "") {
      setPendingChangePercent(null);
      setPendingMetric("");
      setLimitExceeded(false);
      setLimitAck(false);
      return;
    }

    // Rank tab: always require user confirmation, no limit math
    if (priceType === 3) {
      setLimitExceeded(true);
      setLimitAck(false);
      setPendingChangePercent(null);
      setPendingMetric("rank");
      return;
    }

    const limit = Number(max_reprice_limit);
    const numericCandidate = Number(candidateVal);

    if (!Number.isFinite(numericCandidate) || !Number.isFinite(limit) || limit <= 0) {
      setLimitExceeded(false);
      setLimitAck(true);
      setPendingChangePercent(null);
      setPendingMetric("");
      return;
    }

    const { maxIncrease, metric } = computeMaxIncreasePercent(candidateVal);
    const exceeded = Number.isFinite(maxIncrease) && Math.abs(maxIncrease) > limit;

    setPendingChangePercent(Number.isFinite(maxIncrease) ? maxIncrease : null);
    setPendingMetric(metric);
    setLimitExceeded(exceeded);
    setLimitAck(!exceeded);
  };

  // LIMIT CHECK ONLY: compute nextTotal relative to ORIGINAL baseline (not current revised)
const nextTotalForLimit = (stone, candidateVal) => {
  const nVal = Number(candidateVal);
  if (!Number.isFinite(nVal)) return null;

  // Prefer original baseline if available (same idea as PopOver: row.original ?? row)
  const base = stone?.original ?? stone;

  const cts = Number(base["Carat"]);
  const origCents = Number(base["originalTotalPrice"]);
  const origTotal = Number.isFinite(origCents) ? Math.round(origCents / 100) : null;

  if (!Number.isFinite(origTotal) || origTotal === 0) return null;
  if (!Number.isFinite(cts) || cts <= 0) return null;

  // PPC tab -> convert to total
  if (priceType === 0) {
    const origPpc = Number(base["$/Ct"]);
    if (!Number.isFinite(origPpc)) return null;

    const nextPpc = (pricingMethod === "to") ? nVal : (origPpc + nVal);
    if (!Number.isFinite(nextPpc)) return null;

    return nextPpc * cts;
  }

  // Total tab -> IMPORTANT: "by" must add to ORIGINAL total for limit math
  if (priceType === 1) {
    return (pricingMethod === "to") ? nVal : (origTotal + nVal);
  }

  // Rap% tab -> convert to total using Rap $/Ct and carat
  if (priceType === 2) {
    const rapList = Number(base["Rap $/Ct"]);
    const origRap = Number(base["Rap %"]);
    if (!Number.isFinite(rapList) || rapList <= 0) return null;
    if (!Number.isFinite(origRap)) return null;

    const nextRap = (pricingMethod === "to") ? nVal : (origRap + nVal);
    if (!Number.isFinite(nextRap)) return null;

    return rapList * (1 + nextRap / 100) * cts;
  }

  // Rank tab: no limit math here
  return null;
};


  const computeMaxIncreasePercent = (candidateVal) => {
    if (!Array.isArray(selectedItems) || selectedItems.length === 0) {
      return { maxIncrease: 0, metric: "" };
    }

    // Always evaluate limit on Total Price basis using original totals. For PPC/Rap%, convert to total price.
    let metric = "total price";
    let maxIncrease = 0;

    selectedItems.forEach((stone) => {
      const origCents = Number(stone["originalTotalPrice"]);
      const origTotal = Number.isFinite(origCents) && Math.round(origCents / 100);
      console.log("origTotal", origTotal, "stone", stone["Total Price"]);
      if (!Number.isFinite(origTotal) || origTotal === 0) return; // avoid divide by zero

      // Calculate proposed total price for the stone under the current tab/method
      // Calculate proposed total price for LIMIT CHECK using ORIGINAL baseline (not current revised)
      const nextTotal = nextTotalForLimit(stone, candidateVal);

      if (!Number.isFinite(nextTotal)) return;

      const change = ((nextTotal - origTotal) / origTotal) * 100;
      const absChange = Math.abs(change);
      if (absChange > maxIncrease) maxIncrease = change; // consider both directions
    });

    return { maxIncrease, metric };
  };

  const handleValueChange = (nextVal) => {
    // Always reflect the UI change
    setValue(nextVal);
    evaluateLimit(nextVal);
  };


  const calculateNewPPC = (stone, inputVal = value) => {
  const cts = Number(stone["Carat"]);
  const currPpc = Number(stone["$/Ct"]);
  const currRap = Number(stone["Rap %"]);
  const rapList = Number(stone["Rap $/Ct"]);
  const nVal = Number(inputVal);

    if (priceType === 0) {
      if (!Number.isFinite(nVal)) return currPpc;
      return (pricingMethod === "to") ? nVal : currPpc + nVal;
    }

    if (priceType === 1) {
      if (!Number.isFinite(nVal)) return currPpc;
      const newTotal = (pricingMethod === "to") ? nVal : Number(stone["Total Price"]) + nVal;
      return cts ? (newTotal / cts) : currPpc;
    }

    if (priceType === 2) { // editing Rap%
      if (!Number.isFinite(nVal)) return currPpc;
      const newRap = (pricingMethod === "to") ? nVal : currRap + nVal;
      return Number.isFinite(rapList) ? rapList * (1 + newRap / 100) : currPpc;
    }

    // priceType === 3 (Rank)
    if (configSort === 'rap_discount') {
      const newRap = calculateNewRap(stone, inputVal); // now Rank-aware
      return Number.isFinite(rapList) ? rapList * (1 + newRap / 100) : currPpc;
    }

    // If Rank is by PPC and you have a PPC array, map from array similarly:
    if (configSort === 'price_per_carat') {
      // TODO: use getSortedMetricArray(stone, 'price_per_carat') and map like Rap%
    }

    return currPpc;
  };

const calculateNewPrice = (stone, inputVal = value) => {
  const cts = Number(stone["Carat"]);
  const currTotal = Number(stone["Total Price"]);
  const nVal = Number(inputVal);

  if (priceType === 0) { // PPC tab
    if (!Number.isFinite(nVal) || !Number.isFinite(cts)) return currTotal;
    const ppc = (pricingMethod === "to") ? nVal : Number(stone["$/Ct"]) + nVal;
    return ppc * cts;
  }

  if (priceType === 1) { // Total tab
    if (!Number.isFinite(nVal)) return currTotal;
    return (pricingMethod === "to") ? nVal : currTotal + nVal;
  }

  if (priceType === 2) { // Rap% tab
    const rapList = Number(stone["Rap $/Ct"]);
    const currRap = Number(stone["Rap %"]);
    if (!Number.isFinite(rapList) || !Number.isFinite(cts)) return currTotal;
    const newRap = !Number.isFinite(nVal) ? currRap :
      (pricingMethod === "to" ? nVal : currRap + nVal);
    return rapList * (1 + newRap / 100) * cts;
  }

  // priceType === 3 (Rank)
  if (configSort === 'rap_discount') {
    const rapList = Number(stone["Rap $/Ct"]);
    if (!Number.isFinite(rapList) || !Number.isFinite(cts)) return currTotal;
    const newRap = calculateNewRap(stone, inputVal); // rank-aware rap%
    return rapList * (1 + newRap / 100) * cts;
  }

  // If ranking by PPC/Total, implement analogous mapping when you have those arrays.
  return currTotal;
};


  

  const calculateNewRap = (stone, inputVal = value) => {
    const currRap = Number(stone["Rap %"]);
    const nVal = Number(inputVal);

    // If we're *directly* editing Rap% tab:
    if (priceType === 2) {
      if (!Number.isFinite(nVal)) return currRap;
      return (pricingMethod === "to") ? nVal : currRap + nVal;
    }

    // If we're in Rank mode and ranking by rap_discount, map target rank/percentile to a rap%:
    if (priceType === 3 && configSort) {
      const arr = getSortedMetricArray(stone, configSort);
      if (!arr.length) return currRap;

      let implied;
      if (rankType === "rank") {
        implied = valueFromTargetRank(arr, nVal); // nVal is target rank
      } else {
        implied = valueFromPercentile(arr, nVal); // nVal is percentile
      }
      return Number.isFinite(implied) ? implied - 0.1 : currRap;
    }

    // Otherwise, keep as-is (or compute from price if you have rap list $/ct)
    return currRap;
  };

  const buildDetailsUrl = (id) => {
  // Ensure it starts with a leading slash because your server example expects it
  let normalized = id || "";
  if (normalized && !normalized.startsWith("/")) normalized = `/${normalized}`;
  const encoded = encodeURIComponent(normalized);
  return `${process.env.REACT_APP_API_URL}/request_channel_details?request_channel_id=${encoded}`;
};

// Put these inside your component (outside render):

// Direction-aware binary search that returns the insertion index (0-based)
const binarySearch = (arr, target) => {
  if (!Array.isArray(arr) || arr.length === 0) return 0;
  let l = 0, r = arr.length; // half-open [l, r)
  while (l < r) {
    const m = l + Math.floor((r-l)/2); // Math.floor((l+r)/2)
    if (arr[m] < target) l = m + 1; else r = m;
  }
  return l; // first index where arr[i] >= target
};

const getSortedMetricArray = (stone, metric) => {
  // metric: 'rap_discount' | 'price_per_carat' | 'total_price'
  // For now you only showed rap% array:
  if (metric === 'rap_discount') {
    const raw = stone["Rapnet Price Array"];
    const nums = Array.isArray(raw) ? raw.map(Number).filter(Number.isFinite) : [];
    nums.sort((a,b) => a - b); // ensure ASC
    return nums;
  }
  // TODO: plug PPC / Total arrays if you have them
  return [];
};

const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);

// value implied by a target rank (1-based) on an ASC array
const valueFromTargetRank = (arr, targetRank) => {
  if (!Array.isArray(arr) || arr.length === 0) return null;
  const idx = clamp(Math.round(targetRank) - 1, 0, arr.length - 1);
  return arr[idx];
};

// value implied by a target percentile (0..100) on an ASC array
const valueFromPercentile = (arr, p) => {
  if (!Array.isArray(arr) || arr.length === 0) return null;
  const pct = clamp(Number(p) || 0, 0, 100);
  const idx = Math.ceil((pct / 100) * (arr.length)) -1;
  if (idx < 0)  return arr[0]
  return arr[idx];
};

const getSortedPpcArray = (stone) => {
  // Prefer the per-stone RapNet array if available
  let arr = stone["Rapnet Price Array"];
  
  // if (Array.isArray(arr) && arr.length) {
  //   const nums = arr.map(Number).filter(Number.isFinite);
  //   // CRITICAL: Sort ascending for binary search to work
  //   nums.sort((a, b) => a - b);
  //   return nums;
  // }
  
  // Fallback: derive from current visible/selected items
    
  return arr;
};



const estimateNewRank = (stone, inputVal = value) => {
  // For non-Rank tabs (PPC, Total, Rap%), we compute rank change against the Rap% array
  // by converting PPC/Total to an implied Rap% using the stone's Rap $/Ct.
  // For the Rank tab, we respect the user's configSort.

  const cts = Number(stone["Carat"]);
  const rapList = Number(stone["Rap $/Ct"]);

  let metric = configSort;
  if (priceType !== 3) {
    metric = 'rap_discount';
  }

  const arr = getSortedMetricArray(stone, metric);
  if (!arr.length) return Number(stone["Rap Rank"]) || null;

  // Determine the value to rank against the chosen metric array
  let valueToRank;

  if (priceType === 3) {
    // Rank mode: value implied by target rank/percentile (in the chosen metric)
    const nVal = Number(inputVal);
    if (rankType === "rank") valueToRank = valueFromTargetRank(arr, nVal);
    else valueToRank = valueFromPercentile(arr, nVal);
  } else if (priceType === 2) {
    // Editing Rap% directly
    valueToRank = calculateNewRap(stone, inputVal);
  } else if (priceType === 0) {
    // Editing PPC: convert new PPC to implied Rap%
    const newPpc = calculateNewPPC(stone, inputVal);
    const impliedRap = Number.isFinite(rapList) && rapList > 0
      ? ((newPpc / rapList) - 1) * 100
      : Number(stone["Rap %"]);
    valueToRank = impliedRap;
  } else {
    // priceType === 1 (Total): convert new Total to PPC and then to implied Rap%
    const newTotal = calculateNewPrice(stone, inputVal);
    const ppc = Number.isFinite(cts) && cts > 0 ? (newTotal / cts) : Number(stone["$/Ct"]);
    const impliedRap = Number.isFinite(rapList) && rapList > 0
      ? ((ppc / rapList) - 1) * 100
      : Number(stone["Rap %"]);
    valueToRank = impliedRap;
  }

  if (!Number.isFinite(valueToRank)) return Number(stone["Rap Rank"]) || null;

  const idx = binarySearch(arr, valueToRank);
  return clamp(idx + 1, 1, arr.length); // 1-based
};

const getRankDelta = (stone, inputVal = value) => {
  const oldRank = Number(stone["Rap Rank"]);
  const newRank = estimateNewRank(stone, inputVal);
  if (!Number.isFinite(oldRank) || !Number.isFinite(newRank)) return null;
  return newRank - oldRank; // + = worse, - = better
};

useEffect(() => {
  if (!isProcessing || !channelId) return;

  const token = Cookies.get("access_token");
  const url = buildDetailsUrl(channelId);

  const fetchOnce = async () => {
  try {
    const resp = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        'ngrok-skip-browser-warning': 'true',
      },
    });

    // Normalize the server shape
    const details = resp?.data?.channel_details ?? resp?.data ?? {};

    // Parse progress (handles "100.0", "100%", 100)
    let pRaw = details.progress;
    let p = 0;
    if (typeof pRaw === "string") {
      const m = pRaw.match(/(\d+(?:\.\d+)?)/);
      p = m ? parseFloat(m[1]) : 0;
    } else if (typeof pRaw === "number") {
      p = pRaw;
    }

    // Optional flags
    const notice = String(details.notice ?? "");
    const errorMsg = String(details.error ?? "").trim();

    // Update progress bar
    if (Number.isFinite(p)) setProgress(p);

    // Stop conditions
    const doneByProgress = Number.isFinite(p) && p >= 99.9; // tolerate "100.0"
    const doneByNotice = /complete/i.test(notice);          // "Action Complete"
    const hasError = errorMsg.length > 0;

    if (hasError) {
      // stop & surface error
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      setIsProcessing(false);
      dispatch(setToast(true));
      dispatch(setAlertText(errorMsg || "Processing failed"));
      dispatch(setSeverity("error"));
      return;
    }

    if (doneByProgress || doneByNotice) {
      // ✅ done — stop polling
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }

      setIsProcessing(false);

      // Invalidate or refetch depending on filter state
      if (Array.isArray(filters) && filters.length > 0 && accountId) {
        try {
          const args = {
            id: accountId,
            from: page ? pageSize * page : 0,
            size: pageSize,
            asc_desc: sorting?.[0]?.asc_desc ?? 'asc',
            sort_by: sorting?.[0]?.sort_by ?? 'sell_offer_cents',
            search: searchParam,
            sort: sorting,
            state: activeTab === 3 ? 'repriced' : '',
            is_filter_locked: activeTab === 1 ? false : activeTab === 2 ? true : '',
            isSearched: false,
          };
          const body = convertToJsonObject(filters, customFiltersRaw);
          const data = await refetchFiltered({ args, body }).unwrap();
          // Push updated rows into table
          dispatch({ type: 'pricing/setUserTable', payload: data?.data?.map((it) => mapping(it)) || [] });
          dispatch({ type: 'pricing/setPageCount', payload: data?.total_matching_count || 0 });
        } catch (e) {
          // If refetch via mutation fails, fall back to tag invalidation
          dispatch(apiSlice.util.invalidateTags(["priceTable"]));
        }
      } else {
        // No active filters: invalidate table queries to auto-refetch
        dispatch(apiSlice.util.invalidateTags(["priceTable"]));
      }
      // Invalidate matching caches for details panes
      if (selectedItems[0]?.itemIDs !== "All") {
        uniqueLDIDs.forEach((id) => {
          dispatch(apiSlice.util.invalidateTags([`Rapnet matching ${id}`]));
        });
      }

      // Success toast
      dispatch(setToast(true));
      dispatch(setAlertText("Repricing completed successfully!"));
      dispatch(setSeverity("success"));

      // Optional: close dialog
      handleClose();
    }
  } catch (err) {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setIsProcessing(false);
    dispatch(setToast(true));
    dispatch(setAlertText(err.response?.data?.message || "Polling failed"));
    dispatch(setSeverity("error"));
  }
};


  // Kick off immediately, then every 5s
  fetchOnce();
  pollRef.current = setInterval(fetchOnce, 5000);

  // Cleanup on unmount / dependency change
  return () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };
}, [isProcessing, channelId]); // eslint-disable-line react-hooks/exhaustive-deps


  const handleBulkReprice = async () => {
    try {
      const buttonClickedEvent = {
        event_type: `v4_bulkrepricing`,
      };
      amplitude.track(buttonClickedEvent);
    } catch {}
    setIsLoading(true);
    setOpen(false);
    const url = `${process.env.REACT_APP_API_URL}/pcp_bulk_repricing`;
    let data;
    
    if (allSelected) {
      data = {
        item_ids: id == "item-details" ? uniquePRR1IDs :[],
        select_all: true,
        is_total_price: priceType == 1? true : false,
        is_price_per_carat: priceType == 0 ? true : false,
        is_rap_discount: priceType == 2? true :false,
        is_rank: priceType == 3 && rankType == "rank"? true : false,
        is_rank_percentile: priceType == 3 && rankType == "percentile"? true : false,
        item_filter: newFilters.item_filter,
        market_filters: newFilters.market_filters,
        custom_data_filters: newFilters.custom_data_filters,
        ml_filters: newFilters.ml_filters,
        cert_stock_number: searchParam,
        value: value,
        force_update: true,
        // Force reprice_type to "to" for Rank/Percentile as required by API
        reprice_type: priceType === 3 ? "to" : pricingMethod,
      };
    } else {
      data = {
        item_ids: id == "item-details" ? uniquePRRIDs :uniqueLDIDs,
        select_all: false,
        is_total_price: priceType == 1? true : false,
        is_price_per_carat: priceType == 0 ? true : false,
        is_rap_discount: priceType == 2? true :false,
        is_rank: priceType == 3 && rankType == "rank"? true : false,
        is_rank_percentile: priceType == 3 && rankType == "percentile"? true : false,
        item_filter: newFilters.item_filter,
        market_filters: newFilters.market_filters,
        custom_data_filters: newFilters.custom_data_filters,
        ml_filters: newFilters.ml_filters,
        cert_stock_number: searchParam,
        value: value,
        force_update: true,
        // Force reprice_type to "to" for Rank/Percentile as required by API
        reprice_type: priceType === 3 ? "to" : pricingMethod,
      };
    }

    if (activeTab === 3) {
      data.state = "repriced";
    }

    if (activeTab === 1 || activeTab === 2) {
      data.is_filter_locked = activeTab === 1 ? false : true;
    }

    let token = Cookies.get("access_token");

    try {
      const response = await axios.post(url, data, {
        headers: {
          Authorization: `Bearer ${token}`,
          'ngrok-skip-browser-warning': 'true',
        },
      });

      setIsLoading(false);

      if (response.data.is_working_in_background) {
        // Background job started
        const reqChannelId = response.data.request_channel_id;
        // Start global progress tracking so it persists across UI changes
        // For item-details, always pass actual LD IDs for invalidation (even when select-all)
        const invalidateLDs = (id === "item-details")
          ? Array.from(new Set((selectedItems || []).map(it => it["PRR ID"])) ).filter(Boolean)
          : uniqueLDIDs;
        dispatch(startProgress({
          channelId: reqChannelId,
          message: response.data.message,
          invalidateLDIDs: invalidateLDs,
          invalidatePRRIDs: uniquePRRIDs,
          selectedAll: allSelected,
          routeId: id,
          successMessage: 'Repricing completed successfully!'
        }));
        // Locally disable UI and close dialog
        setIsProcessing(true);
        setOpen(false);
        // Optional immediate info toast
        dispatch(setToast(true));
        dispatch(setAlertText(response.data.message));
        dispatch(setSeverity('info'));
      } else {
        // Immediate response (small dataset). Refresh data now.
        if (Array.isArray(filters) && filters.length > 0 && accountId) {
          try {
            const args = {
              id: accountId,
              from: page ? pageSize * page : 0,
              size: pageSize,
              asc_desc: sorting?.[0]?.asc_desc ?? 'asc',
              sort_by: sorting?.[0]?.sort_by ?? 'sell_offer_cents',
              search: searchParam,
              sort: sorting,
              state: activeTab === 3 ? 'repriced' : '',
              is_filter_locked: activeTab === 1 ? false : activeTab === 2 ? true : '',
              isSearched: false,
            };
            const body = convertToJsonObject(filters);
            const data = await refetchFiltered({ args, body }).unwrap();
            dispatch({ type: 'pricing/setUserTable', payload: data?.data?.map((it) => mapping(it)) || [] });
            dispatch({ type: 'pricing/setPageCount', payload: data?.total_matching_count || 0 });
          } catch (e) {
            dispatch(apiSlice.util.invalidateTags(["priceTable"]));
          }
        } else {
          dispatch(apiSlice.util.invalidateTags(["priceTable"]));
        }
        // Invalidate matching caches for details panes
        if (selectedItems[0]?.itemIDs !== "All") {
          if(id == "item-details"){
            uniquePRRIDs.forEach(id => {
              dispatch(apiSlice.util.invalidateTags([`Rapnet matching ${id}`]));
            });
          }
          uniqueLDIDs.forEach(id => {
            dispatch(apiSlice.util.invalidateTags([`Rapnet matching ${id}`]));
          });
        }
        dispatch(setToast(true));
        dispatch(setAlertText(response.data.message));
        dispatch(setSeverity("success"));
        handleClose();
      }
    } catch (error) {
      setIsLoading(false);
      dispatch(setToast(true));
      dispatch(setAlertText(error.response?.data?.message || "An error occurred"));
      dispatch(setSeverity("error"));
    }
  }

  const getPriceTypeLabels = () => {
    switch (priceType) {
      case 0:
        return {
          label: pricingMethod === "to" ? "Price per carat ($)" : "Adjust per carat ($)",
          placeholder: "4500",
          description: pricingMethod === "to"
            ? "Set all selected stones to this price per carat"
            : "Add/subtract this amount per carat from current revised prices"
        }
      case 1:
        return {
          label: pricingMethod === "to" ? "Total price ($)" : "Adjust total price ($)",
          placeholder: "5000",
          description: pricingMethod === "to"
            ? "Set each stone to this total price"
            : "Add/subtract this amount from current revised total price"
        }
      case 2:
        return {
          label: pricingMethod === "to" ? "RAP discount (%)" : "Adjust RAP discount (%)",
          placeholder: "-54",
          description: pricingMethod === "to"
            ? "Set RAP discount percentage (negative for discount)"
            : "Adjust current RAP discount by this revised percentage"
        }
      case 3:
        return {
          label: (rankType == "rank") ? "Target Rank" : "Target Rank Percentile",
          placeholder: "4",
          description: rankType == "rank" ? "Set all selected stones to achieve this target rank (e.g., 5 for 5th rank)"  :"Set all selected stones to achieve this rank percentile (e.g, 10 means 10th percentile of 500 stones is rank 50)" 
        }  
      default:
        return { label: "", placeholder: "", description: "" }
    }
  }

  const priceLabels = getPriceTypeLabels()
  const sortToType = {
  rap_discount: 2,
  price_per_carat: 0,
  total_price: 1,
};

// inside your component render:
const effectiveType = priceType === 3 ? (sortToType[configSort] ?? 0) : priceType;
const labelFor = (t) => (t === 0 ? "PPC" : t === 1 ? "Total" : "RAP %");
  // Connection status display

  return (
    <div className='w-max flex justify-center'>
      <div
        className={cn(
          "self-end flex gap-[0.200rem] items-center justify-center  h-8  text-[#1E90FF] font-medium text-[0.75rem] w-max",
          selectedItems.length > 0
            ? "bg-transparent text-[#1E90FF] cursor-pointer"
            : "bg-transparent opacity-40 border-opacity-40 cursor-not-allowed text-[#1E90FF]"
        )}
        onClick={handleClickOpen}
        disabled={selectedItems.length === 0}
      >
        <BadgePercent className="h-4 w-4" /> Bulk Reprice
      </div>

      <Dialog
        open={open}
        onClose={!isProcessing ? handleClose : undefined} // Prevent closing during processing
        PaperProps={{
          component: 'form',
          className: "p-2",
          onSubmit: (event) => {
            event.preventDefault();
          },
          sx: {
            width: '700px',
            maxWidth: '700px',
            minWidth: '700px',
            minHeight: '82vh',
            overflow: 'visible'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          mb: 2,
          borderBottom: '1px solid #e0e0e0'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Bulk Repricing
          </Typography>
          {!isProcessing && (
            <div
              className="px-1 transition-colors duration-200 cursor-pointer"
              onClick={handleClose}
              type="button"
            >
              <svg
                className="h-5 w-5 text-gray-500 hover:text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
          )}
        </DialogTitle>
        
        <DialogContent sx={{ overflow: 'auto', p: 3 }}>
          

          <Grid container spacing={4}>
            {/* Pricing Configuration */}
            <Grid item xs={12} lg={6}>
              <Box 
               sx={{ 
                display: 'flex',
                flexDirection: 'column',
                gap: 3, 
                width: '100%',
                minWidth: "273px",
                maxWidth: "275px%",
                opacity: isProcessing ? 0.6 : 1,
                pointerEvents: isProcessing ? 'none' : 'auto'
                }}>
                <Box>
                  <h6 className='mb-3 text-sm font-semibold text-gray-700'>
                    Price Type
                  </h6>
                  <TailwindTabs
                    value={priceType}
                    onChange={(e, newValue) => {
                      setPriceType(newValue);
                      // When switching to Rank tab, API expects reprice_type = "to"
                      if (newValue === 3) setPricingMethod("to");
                      evaluateLimit(value);
                    }}
                  />
                </Box>
                <FormControl component="fieldset">
                  <h6 className='mb-3 text-sm font-semibold text-gray-700'>
                    Pricing Method
                  </h6>
                  { priceType === 3 ? (
                    <RadioGroup
                    value={rankType}
                    onChange={(e) => setRankType(e.target.value)}
                  >
                  
                    <FormControlLabel value="rank" control={<Radio size="small" />} sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.9rem' } }} label="Set to exact rank" />
                    <FormControlLabel value="percentile" control={<Radio size="small" />} sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.9rem' } }} label="Set to rank percentile" />
                    
                    
                  </RadioGroup>
                  ): (
                  <RadioGroup
                    value={pricingMethod}
                    onChange={(e) => {
                      setPricingMethod(e.target.value);
                      evaluateLimit(value);
                    }}
                  >
                    <FormControlLabel value="to" control={<Radio size="small" />} sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.9rem' } }} label="Set price to" />
                    <FormControlLabel value="by" control={<Radio size="small" />} sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.9rem' } }} label="Adjust price by" />
                    
                  </RadioGroup>
                  )
                }  
                </FormControl>
                <Box>
                  <div className="mt-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {priceLabels.label}
                  </label>
                  <input
                    type="number"
                    placeholder={priceLabels.placeholder}
                    value={value}
                    onChange={(e) => handleValueChange(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[36px]"
                  />
                  <p className="text-xs text-gray-500 mt-1 pl-1 break-words min-h-[40px]">
                    {priceLabels.description}
                  </p>
                  {limitExceeded && (
                    <div className=" border border-red-700 bg-red-50 rounded-md p-2">
                      {priceType === 3 ? (
                        <>
                          <p className="text-xs text-red-900">
                            Rank repricing may exceed your price limit of +/-{max_reprice_limit ?? "-"}%.
                          </p>
                          {/* <p className="text-xs text-slate-600">
                            Please confirm before applying.
                          </p> */}
                        </>
                      ) : (
                        <>
                          <p className="text-xs text-red-900">
                            This change exceeds your price limit of +/-{max_reprice_limit ?? "-"}%.
                          </p>
                          <p className="text-xs text-slate-600">
                            Maximum change in {pendingMetric || "price"} is{" "}
                            {Number.isFinite(pendingChangePercent)
                              ? `${pendingChangePercent.toFixed(1)}%`
                              : "--"}
                            .
                          </p>
                        </>
                      )}
                    </div>
                  )}
                </div>

                </Box>
              </Box>
            </Grid>

            {/* Selected Stones Preview */}
            <Grid item xs={12} lg={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Selected Stones ({selectedItems.length > 0 ? (allSelected ? "All" : id== "item-details" ?uniquePRRIDs.length : uniqueLDIDs.length) : 0})
                  </Typography>
                  <Chip label={`${selectedItems.length > 0 ? (allSelected ? "All" : id== "item-details" ? uniquePRRIDs.length : uniqueLDIDs.length) : 0} stones`} variant="outlined" />
                </Box>
                
                <Box sx={{ maxHeight: 444, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {selectedItems.slice(0, 3).map((stone) => {
                    const newTotal   = calculateNewPrice(stone);
                    const newPpc     = calculateNewPPC(stone);
                    const newRap     = calculateNewRap(stone);
                    
                    const deltaTotal = newTotal - stone["Total Price"];
                    const deltaPpc   = newPpc - stone["$/Ct"];
                    const deltaRap   = newRap - stone["Rap %"];

                    const rankDelta = getRankDelta(stone);
                    const newRank = estimateNewRank(stone);
                    
                    // Color logic: Lower rank number = better (green), Higher = worse (red)
                    const rankColor = rankDelta == null 
                      ? 'text.secondary' 
                      : (rankDelta < 0 ? 'success.main' : rankDelta > 0 ? 'error.main' : 'text.secondary');
                    


                    return (
                      <Card key={stone['LD ID']} variant="outlined">
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <div className='flex justify-between items-center mb-1'>
                            <h6 className='text-[0.69rem] font-semibold text-gray-700'>
                              {stone["Stock No"]}
                            </h6>
                            <div className='flex items-center justify-center'>
                              <h6 className='text-[0.69rem] text-gray-600'>Current {labelFor(effectiveType)}:</h6>
                                <h6 className='text-[0.69rem] font-semibold px-2 text-gray-700'>
                                  {effectiveType === 0
                                    ? `$${stone["$/Ct"]}`
                                    : effectiveType === 1
                                      ? `$${Number(stone["Total Price"]).toFixed(1)}`
                                      : `${Number(stone["Rap %"]).toFixed(1)}%`}
                                </h6>
                            </div>
                                {/* <p className='text-[0.7rem] text-gray-600'>Rank# {stone["Rap Rank"] || ''}</p> */}
                            <h6 className='text-[0.69rem] font-semibold px-2 text-gray-700'>
                              {stone["Carat"]}ct
                            </h6>
                          </div>
                          <div className='flex justify-between gap-1'>
                            <div className='flex justify-between' container spacing={1} sx={{ mb: 1 }}>
                              
                              <div className='flex flex-col gap-0.5 items-start' item xs={6}>
                                <div className='flex gap-1 items-center justify-start text-left'>
                                  <p className='text-[0.69rem] text-gray-600'>New {labelFor(effectiveType)}:</p>
                                  <p className='text-[0.69rem] flex items-center justify-center font-semibold  text-gray-700'>
                                    {value === "" || isNaN(value) ? (
                                      <p className="text-gray-400">-</p>
                                    ) : effectiveType === 0 ? (
                                      `${newPpc >= 0 ? '+' : '-'}$${Math.abs(newPpc).toFixed(1)}`
                                    ) : effectiveType === 1 ? (
                                      `${newTotal >= 0 ? '+' : '-'}$${Math.abs(newTotal).toFixed(1)}`
                                    ) : (
                                      `${Math.ceil(newRap)}%`
                                    )}
                                  </p>
                               </div> 
                               <div className='flex gap-1 items-center justify-start text-left'>
                                  <div className='flex gap-1 items-center'>
                              <p className='text-[0.7rem] text-gray-600 '>
                                {priceType === 3 ? `${labelFor(effectiveType)} Change: ` : `${labelFor(effectiveType)} Change : `}
                              </p>
                              {value === "" || isNaN(value) ? (
                                <p className="text-gray-400 text-[0.7rem]">-</p>
                              ) : (                                
                                <p
                                  className={`text-[0.7rem] text-gray-600 ${
                                    (effectiveType === 2 ? deltaRap : effectiveType === 0 ? deltaPpc : deltaTotal) >= 0
                                      ? 'text-green-600'
                                      : 'text-red-600'
                                  }`}
                                >
                                  {effectiveType === 0
                                    ? `${deltaPpc >= 0 ? '+' : '-'}$${Math.abs(deltaPpc).toFixed(0)}`
                                    : effectiveType === 1
                                    ? `${deltaTotal >= 0 ? '+' : '-'}$${Math.abs(deltaTotal).toFixed(0)}`
                                    : `${deltaRap >= 0 ? '+' : '-'}${Math.abs(deltaRap).toFixed(0)}%`}
                                </p>

                              )}
                                
                            </div>
                          </div>  
                              </div>
                            </div>
                            <div className='flex justify-between items-center'>
                             <div className='flex flex-col gap-0.5 items-start' item xs={6}>
                                <div className='flex gap-1 items-center justify-start text-left'>
                                  <p className='text-[0.7rem] text-gray-600'>New Rank: {value == "" ? "-" : newRank}</p>
                                </div> 
                            <div className='flex gap-1 items-center justify-start text-left'>
                              <div className='flex gap-1 items-center'>
                                <p className='text-[0.7rem] text-gray-600 '>
                                Rank Change:
                                </p>
                                {value === "" || isNaN(value) ? (
                                  <span className="text-gray-400 text-xs">-</span>
                                ) : (
                                <p
                                    className={`text-[0.7rem] ${
                                      rankDelta == null
                                        ? 'text-gray-600'
                                        : rankDelta < 0
                                        ? 'text-green-600'
                                        : rankDelta > 0
                                        ? 'text-red-600'
                                        : 'text-gray-600'
                                    }`}
                                  >
                                    ({rankDelta > 0 ? '+' : rankDelta === 0 ? '' : '-'}{Math.abs(rankDelta)})
                                  </p>
                              )}
                                  
                              </div>
                          </div>  
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )
                  })}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions>
          <button
            className={cn(
              "rounded-md flex gap-[0.200rem] items-center justify-center px-3 h-8 border-solid border-[1px] border-[#1E90FF] text-[#1E90FF] font-medium text-[0.75rem] w-max bg-transparent",
              isProcessing && "opacity-50 cursor-not-allowed"
            )}
            onClick={handleClose}
            disabled={isLoading || isProcessing}
          >
            Cancel
          </button>
          <button
            className={cn(
              "rounded-md flex gap-[0.200rem] items-center justify-center px-4 hover:bg-blue-300 h-8 border-none font-medium text-[0.75rem] w-max",
              (isLoading || isProcessing || value === "" || isNaN(value))
                ? "bg-blue-300 cursor-not-allowed"
                : "bg-[#A4D9FF] hover:bg-blue-300"
            )}
            onClick={() => {setConfirmOpen(true);}} 
            type="submit" 
            disabled={isLoading || isProcessing || value === "" || isNaN(value)}
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Loading...
              </>
            ) : isProcessing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Processing...
              </>
            ) : (
              "Reprice"
            )}
          </button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={confirmOpen}
        onClose={() => { if (!isLoading && !isProcessing) setConfirmOpen(false); }}
        PaperProps={{ sx: { maxWidth: 480, width: 400 } }}
      >
            {/* Header */}
            <div className="flex items-center justify-between px-6 pt-4 pb-2 border border-b border-gray-200 ">
              <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                Confirm Bulk Repricing
              </h2>
              <div
                onClick={handleClose}
                disabled={isLoading || isProcessing}
                className=" transition-colors"
              >
                <X size={20} />
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-4 space-y-4">
              {/* Warning Alert */}
              <div className="border border-amber-700 bg-amber-50  rounded-lg p-3 flex items-start justify-between gap-3">
                <AlertTriangle className="h-4 w-4 text-amber-600  flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-900 ">
                  This action cannot be undone. Prices will be updated immediately.
                </p>
              </div>
              {(limitExceeded && !limitAck ) && (
                  <div className="border border-red-700 bg-red-50 rounded-lg p-3 flex items-start gap-3">
                    <AlertTriangle className="h-4 w-4 text-red-600  flex-shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      {priceType === 3 ? (
                        <>
                          <p className="text-xs text-red-900">
                            Rank repricing may exceed your price limit of +/-{max_reprice_limit ?? "-"}%
                          </p>
                          <p className="text-xs text-slate-600">
                            Click Proceed to continue.
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="text-xs text-red-900">
                            This change exceeds your price limit of +/-{max_reprice_limit ?? "-"}%.
                          </p>
                          <p className="text-xs text-slate-600">
                            Maximum change in {pendingMetric || "price"} is{" "}
                            {Number.isFinite(pendingChangePercent)
                              ? `${pendingChangePercent.toFixed(1)}%`
                              : "--"}
                            . Click Proceed to continue.
                          </p>
                        </>
                      )}
                    </div>
                  </div>
              )}
              {/* Action Summary */}
              <div className="space-y-3">
                <p className="text-xs text-slate-500 ">
                  You are about to apply bulk repricing to:
                </p>

                {/* Summary Box */}
                <div className="bg-slate-50  rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between ">
                    <span className="text-sm font-medium ">
                      Stones affected
                    </span>
                    <p className="text-sm font-medium text-slate-900 ">
                      {selectedItems.length > 0 ? (allSelected ? 'all selected' : (id == "item-details" ? `${uniquePRRIDs?.length || 0}` : `${uniqueLDIDs?.length || 0}`)) : 0} stones
                    </p>
                  </div>
                  <hr className="border-t border-gray-600 " />


                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs  ">
                        Pricing type
                      </span>
                      <span className="text-xs  ">
                        {priceType === 0 ? 'Price per Carat' : priceType === 1 ? 'Total Price' : priceType === 2 ? 'Rap %' : (rankType === 'rank' ? 'Rank' : 'Rank Percentile')}
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-xs  ">
                        Method
                      </span>
                      <span className="text-xs  ">
                        {priceType === 3 ? (rankType === 'rank' ? 'Set to exact rank' : 'Set to rank percentile') : (pricingMethod === 'to' ? 'Set to' : 'Adjust by')}
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-xs ">
                        Value
                      </span>
                      <span className="text-xs font-semibold">
                        {(priceType == 0 || priceType == 1) ? `${value >= 0 ? '+' : '-'}$${Math.abs(value)}` :String(value)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border border-t border-slate-200">
              <div
                onClick={() => setConfirmOpen(false)}
                disabled={isLoading || isProcessing}
                className='bg-transparent  rounded-md text-[0.8rem] px-3 py-[0.18rem] font-medium text-black border-solid border-[1.25px] cursor-pointer'
              >
                Cancel
              </div>
              <div
                onClick={() => {handleBulkClose(); setConfirmOpen(false); handleBulkReprice(); }}
                disabled={isLoading || isProcessing || value === "" || isNaN(value)}
                className='bg-blue-500 rounded-md text-[0.8rem] px-3 py-1 font-medium text-white cursor-pointer'
              >
                Proceed
              </div>
            </div>
      </Dialog>
    </div>
  );
}

export default BulkRepricing;
